import { z } from 'zod';
import { isoWeek } from './dates';
import { dateSchema } from './fields';
import { formatDate } from './format';
import { idSchema } from './ids';
import { compareYearMonth, type YearMonth, yearMonthSchema } from './period';

// ───────────────────────────── Effective hours (FR-TS-020…023) ─────────────────────────────

/** Approved worklogs of a project by resource and month, newest month first (gaps included). */
export const effectiveHoursSchema = z.object({
  months: z.array(yearMonthSchema),
  rows: z.array(
    z.object({
      key: z.string(),
      resourceId: idSchema.nullable(),
      name: z.string(),
      /** The resource card's rate; `null` without a resource or without `finance.view`. */
      rate: z.string().nullable(),
      /** Aligned with `months`. */
      hours: z.array(z.number()),
    }),
  ),
});
export type EffectiveHours = z.infer<typeof effectiveHoursSchema>;

/** Every month from the newest to the oldest one that has data (FR-TS-020). */
export function monthsNewestFirst(withData: readonly YearMonth[]): YearMonth[] {
  if (withData.length === 0) return [];
  const sorted = [...withData].sort(compareYearMonth);
  const out: YearMonth[] = [];
  for (let m = sorted.at(-1)!; compareYearMonth(m, sorted[0]!) >= 0;) {
    out.push(m);
    m = m.month === 1 ? { year: m.year - 1, month: 12 } : { year: m.year, month: m.month - 1 };
  }
  return out;
}

// ───────────────────────────── Laika pārskats (FR-OVR-008…012) ─────────────────────────────

export const TIME_GROUPS = ['day', 'week', 'quarter', 'year'] as const;
export type TimeGroup = (typeof TIME_GROUPS)[number];
/** Worklog review filter: without rejected (default), approved only, all (FR-OVR-011). */
export const TIME_STATUS_FILTERS = ['active', 'approved', 'all'] as const;
export const TIME_MODES = ['person', 'project'] as const;
export type TimeMode = (typeof TIME_MODES)[number];

const periodDate = z.string().min(1, { message: 'Norādiet abus datumus.' }).pipe(dateSchema);

/** The page checks the period before asking (FR-OVR-008). */
export const timePeriodSchema = z
  .object({ from: periodDate, to: periodDate })
  .refine((p) => p.from <= p.to, { path: ['from'], message: '"No" datums nedrīkst būt vēlāks par "Līdz".' });

export const timeOverviewQuerySchema = z
  .object({
    from: periodDate,
    to: periodDate,
    group: z.enum(TIME_GROUPS).default('day'),
    status: z.enum(TIME_STATUS_FILTERS).default('active'),
    mode: z.enum(TIME_MODES).default('person'),
  })
  .refine((p) => p.from <= p.to, { path: ['from'], message: '"No" datums nedrīkst būt vēlāks par "Līdz".' });

const timeRowSchema = z.object({
  key: z.string(),
  /** Person (resource or Jira author) or project (PMAI project or Jira project name). */
  name: z.string(),
  /** A PMAI project — the name links to its card. */
  projectId: idSchema.nullable(),
  /** `false` — a Jira project not mapped to PMAI ("(nepiesaistīts)"). */
  mapped: z.boolean(),
  /** Hours per bucket key. */
  cells: z.record(z.string(), z.number()),
  total: z.number(),
});

export const timeOverviewSchema = z.object({
  /** Buckets with data, chronological. */
  buckets: z.array(z.string()),
  rows: z.array(timeRowSchema.extend({ rows: z.array(timeRowSchema) })),
  columnTotals: z.record(z.string(), z.number()),
  total: z.number(),
  worklogs: z.number().int(),
});
export type TimeOverview = z.infer<typeof timeOverviewSchema>;

/** Bucket key of a date: the date, "2026-W41", "2026-Q4" or "2026" (FR-OVR-010). */
export function timeBucket(date: string, group: TimeGroup): string {
  const year = date.slice(0, 4);
  switch (group) {
    case 'day':
      return date;
    case 'week': {
      const w = isoWeek(date);
      return `${w.year}-W${String(w.week).padStart(2, '0')}`;
    }
    case 'quarter':
      return `${year}-Q${Math.floor((Number(date.slice(5, 7)) - 1) / 3) + 1}`;
    case 'year':
      return year;
  }
}

/** "05.10.2026", "2026 N41", "2026 Q4", "2026" — column headers (FR-OVR-010). */
export function timeBucketLabel(key: string, group: TimeGroup): string {
  if (group === 'day') return formatDate(key);
  if (group === 'week') {
    const [year, week] = key.split('-W') as [string, string];
    return `${year} N${Number(week)}`;
  }
  return key.replace('-', ' ');
}
